import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AdminLoading from './AdminLoading';

const defaultForm = {
  gst_enabled: false,
  gst_rate_percent: 0,
  gst_label: 'GST',
  shipping_type: 'fixed',
  shipping_fixed_paise: 5000,
  shipping_free_above_paise: '',
  shipping_label: 'Shipping',
  deposit_type: 'percent',
  deposit_value: 50,
  deposit_label: 'Deposit',
};

export default function AdminCheckoutSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(defaultForm);

  const load = () => {
    setLoading(true);
    setError('');
    api('/api/admin/settings/checkout')
      .then((data) => {
        setForm({
          gst_enabled: !!data.gst_enabled,
          gst_rate_percent: data.gst_rate_percent != null ? data.gst_rate_percent : 0,
          gst_label: data.gst_label ?? 'GST',
          shipping_type: data.shipping_type ?? 'fixed',
          shipping_fixed_paise: data.shipping_fixed_paise != null ? data.shipping_fixed_paise : 5000,
          shipping_free_above_paise: data.shipping_free_above_paise != null ? String(data.shipping_free_above_paise / 100) : '',
          shipping_label: data.shipping_label ?? 'Shipping',
          deposit_type: data.deposit_type ?? 'percent',
          deposit_value: data.deposit_value != null ? (data.deposit_type === 'fixed' ? data.deposit_value / 100 : data.deposit_value) : 50,
          deposit_label: data.deposit_label ?? 'Deposit',
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      gst_enabled: form.gst_enabled,
      gst_rate_percent: Number(form.gst_rate_percent) || 0,
      gst_label: form.gst_label.trim() || 'GST',
      shipping_type: form.shipping_type,
      shipping_fixed_paise: Number(form.shipping_fixed_paise) || 0,
      shipping_free_above_paise: form.shipping_free_above_paise !== '' ? Math.round(Number(form.shipping_free_above_paise) * 100) : null,
      shipping_label: form.shipping_label.trim() || 'Shipping',
      deposit_type: form.deposit_type,
      deposit_value: form.deposit_type === 'per_product' ? 0 : form.deposit_type === 'fixed' ? Math.round(Number(form.deposit_value) * 100) : Math.round(Number(form.deposit_value) || 0),
      deposit_label: form.deposit_label.trim() || 'Deposit',
    };
    try {
      await api('/api/admin/settings/checkout', { method: 'PUT', body: JSON.stringify(payload) });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader title="Checkout / Tax & Shipping" subheader="GST, shipping, and deposit settings. Cart totals and orders use these values." />
      <CardContent>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>GST / Tax</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={form.gst_enabled} onChange={(e) => setForm((f) => ({ ...f, gst_enabled: e.target.checked }))} />}
              label="Enable GST"
            />
            <TextField size="small" type="number" label="Rate %" value={form.gst_rate_percent} onChange={(e) => setForm((f) => ({ ...f, gst_rate_percent: e.target.value }))} inputProps={{ min: 0, max: 100, step: 0.01 }} sx={{ width: 100 }} />
            <TextField size="small" label="Label" value={form.gst_label} onChange={(e) => setForm((f) => ({ ...f, gst_label: e.target.value }))} placeholder="GST" sx={{ width: 120 }} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Shipping</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField size="small" select SelectProps={{ native: true }} label="Type" value={form.shipping_type} onChange={(e) => setForm((f) => ({ ...f, shipping_type: e.target.value }))} sx={{ minWidth: 140 }}>
              <option value="fixed">Fixed amount</option>
              <option value="free_above">Free above order value</option>
              <option value="slab">Slab (advanced)</option>
            </TextField>
            <TextField size="small" type="number" label="Fixed (₹)" value={form.shipping_fixed_paise / 100} onChange={(e) => setForm((f) => ({ ...f, shipping_fixed_paise: Math.round(parseFloat(e.target.value || 0) * 100) }))} inputProps={{ min: 0, step: 0.01 }} sx={{ width: 100 }} />
            {form.shipping_type === 'free_above' && (
              <TextField size="small" type="number" label="Free above (₹)" value={form.shipping_free_above_paise} onChange={(e) => setForm((f) => ({ ...f, shipping_free_above_paise: e.target.value }))} placeholder="e.g. 500" sx={{ width: 140 }} />
            )}
            <TextField size="small" label="Label" value={form.shipping_label} onChange={(e) => setForm((f) => ({ ...f, shipping_label: e.target.value }))} placeholder="Shipping" sx={{ width: 120 }} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Deposit</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField size="small" select SelectProps={{ native: true }} label="Type" value={form.deposit_type} onChange={(e) => setForm((f) => ({ ...f, deposit_type: e.target.value }))} sx={{ minWidth: 140 }}>
              <option value="percent">Percent of order</option>
              <option value="fixed">Fixed amount (₹)</option>
              <option value="per_product">Per product (security deposit)</option>
            </TextField>
            {form.deposit_type !== 'per_product' && (
              <TextField size="small" type="number" label={form.deposit_type === 'percent' ? 'Value %' : 'Value (₹)'} value={form.deposit_value} onChange={(e) => setForm((f) => ({ ...f, deposit_value: e.target.value }))} inputProps={{ min: 0 }} sx={{ width: 120 }} />
            )}
            <TextField size="small" label="Label" value={form.deposit_label} onChange={(e) => setForm((f) => ({ ...f, deposit_label: e.target.value }))} placeholder="Deposit" sx={{ width: 120 }} />
          </Box>

          <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
        </Box>
      </CardContent>
    </Card>
  );
}
